'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Card, Typography, useMediaQuery } from '@mui/material';

import { isMarketResolved } from '@/utils';
import { FeaturedMarketSlide } from './FeaturedMarketSlide';

const MAX_SLIDES = 8;

interface FeaturedMarketsCarouselProps {
  /** Live events to feature — pass the home grid's events so it stays in sync. */
  events?: any[];
}

/**
 * Featured markets carousel — a Polymarket-style hero rail showing one featured
 * market at a time with its outcomes and a live multi-outcome price chart, with
 * prev/next navigation and dots. Fed by the home page's live trending events; only
 * the active slide fetches chart data. Frontend-only, additive.
 */
export const FeaturedMarketsCarousel = ({ events: eventsProp = [] }: FeaturedMarketsCarouselProps) => {
  const [active, setActive] = useState(0);
  const [noData, setNoData] = useState<Set<string>>(new Set());
  const [paused, setPaused] = useState(false);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const events = useMemo(() => {
    // Keep only genuinely live markets — the feed can include old resolved ones
    // (e.g. 2022 games) that are `closed: true` even when umaResolutionStatus is empty.
    const isLive = (m: any) =>
      !!m && m.closed !== true && m.active !== false && !isMarketResolved(m);

    // Richer markets (multi-outcome / negRisk events, e.g. "World Cup Winner")
    // make better features and carry deeper price history than one-off game props.
    const richness = (ev: any) => (ev?.negRisk || (ev?.markets?.length || 0) > 1 ? 1 : 0);

    return [...(eventsProp || [])]
      .filter((ev) => (ev?.markets || []).some(isLive))
      // Feature multi-outcome markets first, then by volume — the chart-rich ones.
      .sort((a, b) => {
        const rd = richness(b) - richness(a);
        if (rd !== 0) return rd;
        return (Number(b?.volume) || 0) - (Number(a?.volume) || 0);
      })
      .slice(0, MAX_SLIDES);
  }, [eventsProp]);

  // Only feature markets that actually have a live chart — empties get skipped.
  const usable = useMemo(() => {
    const u = events.filter((e) => !noData.has(String(e.id)));
    return u.length ? u : events;
  }, [events, noData]);

  const markNoData = useCallback((id: string) => {
    setNoData((prev) => {
      if (prev.has(id)) return prev;
      const n = new Set(prev);
      n.add(id);
      return n;
    });
  }, []);

  // Gentle auto-rotate — paused on hover and when reduced motion is preferred.
  useEffect(() => {
    if (usable.length <= 1 || paused || prefersReducedMotion) return;
    const t = setInterval(() => setActive((i) => (i + 1) % usable.length), 8000);
    return () => clearInterval(t);
  }, [usable.length, paused, prefersReducedMotion]);

  if (usable.length === 0) return null;

  const clamped = active % usable.length;
  const event = usable[clamped];
  const prev = usable[(clamped - 1 + usable.length) % usable.length];
  const next = usable[(clamped + 1) % usable.length];

  const go = (dir: number) => setActive(() => (clamped + dir + usable.length) % usable.length);

  return (
    <Card
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        backgroundImage: 'none',
        boxShadow: 'none',
        p: { xs: 2.5, md: 3 },
        mb: 3,
      }}
    >
      <FeaturedMarketSlide
        key={event.id}
        event={event}
        onNoData={() => markNoData(String(event.id))}
      />

      {/* Navigation */}
      {usable.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mt: 2.5,
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <NavButton dir="prev" label={prev?.title} onClick={() => go(-1)} />

          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
            {usable.map((_, i) => (
              <Box
                key={i}
                component="button"
                aria-label={`Go to featured market ${i + 1}`}
                onClick={() => setActive(i)}
                sx={{
                  border: 'none',
                  p: 0,
                  cursor: 'pointer',
                  width: i === clamped ? 22 : 8,
                  height: 8,
                  borderRadius: 999,
                  bgcolor: i === clamped ? 'primary.main' : 'divider',
                  transition: 'all 0.2s ease',
                }}
              />
            ))}
          </Box>

          <NavButton dir="next" label={next?.title} onClick={() => go(1)} />
        </Box>
      )}
    </Card>
  );
};

const NavButton = ({
  dir,
  label,
  onClick,
}: {
  dir: 'prev' | 'next';
  label?: string;
  onClick: () => void;
}) => (
  <Box
    component="button"
    onClick={onClick}
    aria-label={dir === 'prev' ? 'Previous market' : 'Next market'}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      maxWidth: { xs: 90, sm: 200 },
      border: '1px solid',
      borderColor: 'divider',
      bgcolor: 'transparent',
      cursor: 'pointer',
      borderRadius: 999,
      px: 1.25,
      py: 0.75,
      color: 'text.secondary',
      fontFamily: 'inherit',
      transition: 'all 0.15s ease',
      '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
    }}
  >
    {dir === 'prev' && <ChevronLeftIcon sx={{ fontSize: 18, flexShrink: 0 }} />}
    <Typography
      variant="caption"
      sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    >
      {label || (dir === 'prev' ? 'Prev' : 'Next')}
    </Typography>
    {dir === 'next' && <ChevronRightIcon sx={{ fontSize: 18, flexShrink: 0 }} />}
  </Box>
);
